import { appConfig, type AppConfig } from "../config";
import { captureAttribution, clearMarketingAttribution } from "../analytics/attribution";
import { apiClient, type ApiClient } from "../integrations/api";

const CONSENT_STORAGE_KEY = "conan.quiz.consent.v1";

export interface ConsentState {
  readonly analytics: boolean;
  readonly marketing: boolean;
  readonly policyVersion: string;
  readonly updatedAt: string;
}

export interface ConsentChoice {
  analytics: boolean;
  marketing: boolean;
  policyVersion?: string;
}

export interface ConsentSession {
  sessionId: string;
  sessionToken: string;
}

export type ConsentRemoteStatus = "synced" | "not-requested";

export interface ConsentUpdateResult {
  state: ConsentState;
  remote: ConsentRemoteStatus;
}

export type ConsentListener = (state: ConsentState, previous: ConsentState) => void;

export interface ConsentManagerOptions {
  config?: AppConfig;
  api?: ApiClient;
  storage?: Storage;
  now?: () => Date;
}

export class ConsentSyncError extends Error {
  readonly state: ConsentState;

  constructor(state: ConsentState, options?: ErrorOptions) {
    super("Consent was applied locally but could not be synchronized with the server", options);
    this.name = "ConsentSyncError";
    this.state = state;
  }
}

function browserStorage(): Storage | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean";
}

function parseStoredConsent(raw: string | null, policyVersion: string): ConsentState | undefined {
  if (!raw) return undefined;
  try {
    const value = JSON.parse(raw) as unknown;
    if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
    const object = value as Record<string, unknown>;
    if (
      !isBoolean(object.analytics)
      || !isBoolean(object.marketing)
      || object.policyVersion !== policyVersion
      || typeof object.updatedAt !== "string"
      || Number.isNaN(Date.parse(object.updatedAt))
    ) return undefined;
    return {
      analytics: object.analytics,
      marketing: object.marketing,
      policyVersion,
      updatedAt: object.updatedAt,
    };
  } catch {
    return undefined;
  }
}

export class ConsentManager {
  private readonly config: AppConfig;
  private readonly api: ApiClient;
  private readonly storage: Storage | undefined;
  private readonly now: () => Date;
  private readonly listeners = new Set<ConsentListener>();
  private current: ConsentState;

  constructor(options: ConsentManagerOptions = {}) {
    this.config = options.config ?? appConfig;
    this.api = options.api ?? apiClient;
    this.storage = options.storage ?? browserStorage();
    this.now = options.now ?? (() => new Date());
    const stored = parseStoredConsent(
      this.safeRead(),
      this.config.consentPolicyVersion,
    );
    const initial = stored ?? {
      analytics: false,
      marketing: false,
      policyVersion: this.config.consentPolicyVersion,
      updatedAt: this.now().toISOString(),
    };
    this.current = {
      ...initial,
      analytics: this.config.analyticsConsentRequired ? initial.analytics : true,
      marketing: this.config.marketingConsentRequired ? initial.marketing : true,
    };
  }

  getState(): ConsentState {
    return { ...this.current };
  }

  allowsAnalytics(state: ConsentState = this.current): boolean {
    return !this.config.analyticsConsentRequired || state.analytics;
  }

  allowsMarketing(state: ConsentState = this.current): boolean {
    return !this.config.marketingConsentRequired || state.marketing;
  }

  subscribe(listener: ConsentListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  apply(choice: ConsentChoice): ConsentState {
    const policyVersion = choice.policyVersion?.trim() || this.config.consentPolicyVersion;
    if (!/^[A-Za-z0-9._-]{1,32}$/.test(policyVersion)) {
      throw new TypeError("Invalid consent policy version");
    }
    const previous = this.current;
    const next: ConsentState = {
      analytics: this.config.analyticsConsentRequired ? choice.analytics === true : true,
      marketing: this.config.marketingConsentRequired ? choice.marketing === true : true,
      policyVersion,
      updatedAt: this.now().toISOString(),
    };
    this.current = next;
    this.safeWrite(next);
    if (!next.marketing) clearMarketingAttribution(this.storage);
    for (const listener of this.listeners) listener(next, previous);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent<ConsentState>("conan:consent-changed", { detail: next }));
    }
    return { ...next };
  }

  async update(
    choice: ConsentChoice,
    session?: ConsentSession,
    signal?: AbortSignal,
  ): Promise<ConsentUpdateResult> {
    const state = this.apply(choice);
    if (!session) return { state, remote: "not-requested" };
    if (!session.sessionId.trim() || !session.sessionToken.trim()) {
      throw new ConsentSyncError(state, { cause: new TypeError("Session credentials are required") });
    }
    try {
      const response = await this.api.updateTrackingConsent(
        session.sessionId,
        session.sessionToken,
        {
          analytics: state.analytics,
          marketing: state.marketing,
          policyVersion: state.policyVersion,
          ...((this.allowsAnalytics(state) || this.allowsMarketing(state))
            ? { attribution: captureAttribution({ includeMarketingCookies: this.allowsMarketing(state) }) }
            : {}),
        },
        signal,
      );
      if (response.analytics !== state.analytics || response.marketing !== state.marketing) {
        throw new Error("Server consent acknowledgement did not match the local choice");
      }
      return { state, remote: "synced" };
    } catch (error) {
      throw new ConsentSyncError(state, { cause: error });
    }
  }

  reset(): ConsentState {
    try {
      this.storage?.removeItem(CONSENT_STORAGE_KEY);
    } catch {
      // The in-memory state is still reset when storage is unavailable.
    }
    return this.apply({ analytics: false, marketing: false });
  }

  private safeRead(): string | null {
    try {
      return this.storage?.getItem(CONSENT_STORAGE_KEY) ?? null;
    } catch {
      return null;
    }
  }

  private safeWrite(state: ConsentState): void {
    try {
      this.storage?.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Consent remains enforced in memory for this page lifecycle.
    }
  }
}

export const consentManager = new ConsentManager();

export function getConsentState(): ConsentState {
  return consentManager.getState();
}

export function hasAnalyticsConsent(state?: ConsentState): boolean {
  return consentManager.allowsAnalytics(state);
}

export function hasMarketingConsent(state?: ConsentState): boolean {
  return consentManager.allowsMarketing(state);
}

export function subscribeToConsent(listener: ConsentListener): () => void {
  return consentManager.subscribe(listener);
}
